import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
} from '@bambuser-ui';
import { styled } from '@linaria/react';
import { COLORS, Icon, DirectionalIcon } from '@livecommerce/design-system';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import Skeleton from 'react-loading-skeleton';

const SORT_ORDERS = {
  ASC: 'asc',
  DESC: 'desc',
};

const StyledArrowIcon = styled(DirectionalIcon)`
  margin-left: 8px;
  visibility: ${(props) => props.visibility};
  flex-shrink: 0;
`;

/**
 * @callback bodyCellFormatter
 * @param {Object} row
 */

/**
 * @callback onBodyRowClick
 * @param {Object} row
 */

/**
 * @callback onHeadCellClick
 * @param {Object} options
 * @param {Object} options.headCell
 * @param {string} options.sortOrder
 */

/**
 * @typedef HeadCellConfig
 * @property {string}             id Identifier that will be used to map the data to the correct head column
 * @property {string}             [label] Text that will be displayed in the head cell
 * @property {Object}             [options] Options that applies to a column and the rows of it
 * @property {string}             [options.defaultSortOrder] Defines in which direction the column should be sorted as default (asc/desc)
 * @property {bodyCellFormatter}  [options.bodyCellFormatter] Function that will be applied to every body cell of the column
 * @property {string}             [options.bodyCellLeftImage] If set, defines which property contains an image url that will be used in the left end of the cell
 * @property {Object}             [options.bodyCellStyle] Additional styling to apply to all cells in the headers column
 * @property {Object}             [options.bodyCellWrapperStyle] Additional styling for the wrapper to apply to all cells in the headers column
 * @property {Object}             [options.bodyCellContentStyle] Additional styling for the content to apply to all cells in the headers column
 * @property {boolean}            [options.loading] When true shows a skeleton in the cell instead of its content
 * @property {string}             [options.subtitle] If set, uses property of provided key to display a subtitle in the cell
 * @property {string}             [options.secondarySortKey] Secondary sort key to be used if two rows in the same column have identical values
 * @property {string}             [options.tooltip] If set, uses property of provided key to display an icon with a tooltip in the beginning of the cell
 * @property {Object}             [options.customStyle] If set, applies the specified style to the head cell in question
 * @property {JSX.Element}        [options.tooltipIcon] Sets which icon should be used for the tooltip
 */

/**
 * @typedef InitialSortConfig
 * @property {string} [sortKey] Initial sort key to be used
 * @property {string} [sortOrder] Initial sort order to be used (asc/desc)
 */

/**
 * @typedef CustomStyleConfig
 * @property {Object} [headCell] Additional styling to apply to each head cell
 * @property {Object} [table] Additional styling to apply to the table
 */

/**
 * @typedef SortableTableProps
 * @property {HeadCellConfig[]}   headCells
 * @property {InitialSortConfig}  [initialSort]
 * @property {boolean}            [loading] If set to true, loading skeletons will be shown
 * @property {onBodyRowClick}     [onBodyRowClick] Callback that is invoked when table body row is clicked
 * @property {onHeadCellClick}    [onHeadCellClick] Callback that is invoked when table head cell is clicked
 * @property {Object[]}           [rows] To be displayed in the table. Each object of the array needs to have keys that maps to head cell id:s.
 * @property {CustomStyleConfig}  customStyle
 * @property {number}             [maxRows] Limit number of rows shown in the table
 */

/**
 * @param {SortableTableProps} props
 */
const SortableTable = ({
  headCells,
  initialSort,
  loading = false,
  onBodyRowClick,
  onHeadCellClick,
  rows,
  customStyle,
  maxRows,
}) => {
  const headCellsById = {};

  if (!headCells || headCells?.length === 0) {
    console.info('At least one head cell has to be provided');
    return null;
  }

  headCells.forEach((headCell) => {
    if (!headCell.hasOwnProperty('id')) {
      console.info('All head cells must have an id');
      return;
    }

    headCellsById[headCell.id] = headCell;
  });

  const [sortKey, setSortKey] = useState(() => initialSort?.sortKey || headCells[0].id);
  const [sortOrder, setSortOrder] = useState(() => initialSort?.sortOrder || SORT_ORDERS.ASC);
  const [sortedRows, setSortedRows] = useState([]);

  // initial sort when rows are first retrieved and when rows are updated (e.g. filtered)
  useEffect(() => {
    if (!rows || rows.length === 0) {
      if (sortedRows.length) setSortedRows([]);
      return;
    }

    const secondarySortKey = getHeadCell(headCellsById, sortKey)?.options?.secondarySortKey;
    const sortFormatter = getHeadCell(headCellsById, sortKey)?.options?.sortFormatter;

    const sorted = sortData({
      data: rows,
      sortKey,
      sortOrder,
      secondarySortKey,
      sortFormatter,
    });

    setSortedRows(sorted);
  }, [rows, sortKey, sortOrder]);

  const handleHeadCellClick = (headCell) => {
    const { id, options: { secondarySortKey } = {} } = headCell;
    let newSortOrder;

    if (id !== sortKey) {
      const headCellDefaultSortOrder = headCell?.options?.defaultSortOrder;
      newSortOrder = SORT_ORDERS[headCellDefaultSortOrder?.toUpperCase()] || SORT_ORDERS.ASC;
    } else {
      newSortOrder = sortOrder === SORT_ORDERS.DESC ? SORT_ORDERS.ASC : SORT_ORDERS.DESC;
    }

    setSortKey(id);
    setSortOrder(newSortOrder);

    if (onHeadCellClick) {
      onHeadCellClick({
        headCell,
        sortOrder: newSortOrder,
      });
    }
  };

  return (
    <Table style={{ ...styles.table, ...customStyle?.table }}>
      <TableHead>
        <HeadRow
          cells={headCells}
          onHeadCellClick={handleHeadCellClick}
          sortKey={sortKey}
          sortOrder={sortOrder}
          customStyle={customStyle?.headCell}
        />
      </TableHead>
      <TableBody>
        {loading
          ? <LoadingSkeletons numberOfColumns={headCells.length} />
          : <BodyRows rows={sortedRows} headCells={headCells} onBodyRowClick={onBodyRowClick} maxRows={maxRows} />}
      </TableBody>
    </Table>
  );
};

/**
 * Renders an image to be used when decorating cells
 * @param {object} props
 * @param {object} props.imageUrl path to image to render - if empty will render a gray box with letters from title
 * @param {object} props.title title to be used when no image is available
 * @returns JSX.Element
 */
const BodyCellLeftImage = ({ imageUrl, title }) => {
  const imageStyle = { ...styles.bodyCellLeftImage };

  if (imageUrl) {
    imageStyle.backgroundImage = `url(${imageUrl})`;
    imageStyle.backgroundSize = 'cover';
  }

  return (
    <div style={imageStyle}>
      {!imageUrl &&
        title
          .split(' ')
          .map(n => n.charAt(0))
          .join('')
          .toUpperCase()}
    </div>
  );
};

/**
 * Renders the rows of the table
 * @param {object} props
 * @param {array} props.rows rows to render
 * @param {array} props.headCells list of columns
 * @param {callable} [props.onBodyRowClick] callback to run when a row is clicked
 * @returns
 */
const BodyRows = ({ rows, headCells, onBodyRowClick, maxRows }) => {
  const { labels } = useLocalization(getLabels);

  if (!rows.length) return (
    <TableRow style={styles.noDataContainer}>
      <TableData>
        {labels.noData}
      </TableData>
    </TableRow>
  );

  const visibleRows = rows.slice(0, maxRows);

  return visibleRows.map((row, rowIndex) => {
    return <BodyRow key={rowIndex} row={row} rowIndex={rowIndex} headCells={headCells} onBodyRowClick={onBodyRowClick} />;
  });
};

/**
 * Renders a single row of the table
 * @param {Object} props
 * @param {Object} props.row
 * @param {number} props.rowIndex
 * @param {Array} props.headCells
 * @param {callable} [props.onBodyRowClick] callback to run when a row is clicked
 * @returns JSX.Element
 */
const BodyRow = ({ row, rowIndex, headCells, onBodyRowClick }) => {
  const [rowHovered, setRowHovered] = useState(false);

  const handleBodyRowClick = () => {
    if (onBodyRowClick) onBodyRowClick(row);
  };

  const handleMouseEnter = () => {
    setRowHovered(true);
  };

  const handleMouseLeave = () => {
    setRowHovered(false);
  };

  return (
    <TableRow
      key={rowIndex}
      data-testid={`row-${rowIndex}`}
      onClick={handleBodyRowClick}
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        ...styles.bodyRow,
        cursor: onBodyRowClick ? 'pointer' : 'default',
        ...( onBodyRowClick && rowHovered && { backgroundColor: COLORS.greyMediumLight } ),
      }}
    >
      {headCells.map((headCell, columnIndex) => {
        const {
          id: column,
          options: {
            bodyCellFormatter,
            bodyCellLeftImage,
            testShowLabel,
            bodyCellStyle,
            bodyCellWrapperStyle,
            bodyCellContentStyle,
            loading,
            subtitle,
            tooltip,
            tooltipIcon,
          } = {},
        } = headCell;

        const cellContent = !!bodyCellFormatter ? bodyCellFormatter(row) : row[column];
        const cellSubtitle = !!subtitle && row[subtitle];
        const cellTooltip = !!tooltip && row[tooltip];
        const cellTitle = (() => {
          let title = '';
          if (typeof cellContent !== 'object') {
            title = `${cellContent}`;
          }
          return `${title}${cellSubtitle ? ` (${cellSubtitle})` : ''}`;
        })();

        const displayTooltip = !!cellTooltip && !!tooltipIcon;

        return (
          <TableData
            key={`${rowIndex}_${columnIndex}`}
            style={{ ...bodyCellStyle }}
          >
            {loading ? (
              <Skeleton />
            ) : (
              <div style={{ ...styles.bodyCellFlexWrapper, ...bodyCellWrapperStyle }} title={cellTitle}>
                {!!bodyCellLeftImage && <BodyCellLeftImage imageUrl={row[bodyCellLeftImage]} title={row[column]} />}
                {row.isTestShow && testShowLabel}
                {displayTooltip && (
                  <span style={styles.tooltipIndicator}>
                    <Tooltip
                      borderRadius="5px"
                      title={cellTooltip}
                      offsetMargin={8}
                      positionStyles={handleTooltipPositionStyles}
                      style={styles.tooltip}
                    >
                      {tooltipIcon}
                    </Tooltip>
                  </span>
                )}
                <span style={{ ...styles.ellipsisContainer, ...bodyCellContentStyle }}>
                  {cellContent}
                  {cellSubtitle && <span style={styles.bodyCellSubtitle}>({cellSubtitle})</span>}
                </span>
              </div>
            )}
          </TableData>
        );
      })}
    </TableRow>
  );
};

/**
 * Renders the row of headers to the table
 * @param {object} props
 * @param {array} props.cells The cells to render
 * @param {callable} props.onHeadCellClick Called when a cell in the header is clicked
 * @param {string} props.sortKey Which key to sort by
 * @param {SORT_ORDERS} props.sortOrder Which direction to sort in (desc/asc)
 * @param {object} [props.customStyle] Additional style to apply to each head cell
 * @returns JSX.Element
 */
const HeadRow = ({ cells, onHeadCellClick, sortKey, sortOrder, customStyle }) => (
  <TableRow
    style={styles.headRow}
  >
    {cells.map((cell, cellIndex) => {
      return (
        <TableHeader
          id={cell.id}
          data-testid={cell.id}
          key={cellIndex}
          onClick={onHeadCellClick.bind(this, cell)}
          style={{ ...styles.headCell, ...cell.options?.customStyle }}
        >
          <div style={{ ...styles.headCellContent, ...customStyle }}>
            {cell.label}
            <StyledArrowIcon
              name="arrow"
              width={16}
              height={16}
              direction={sortOrder === SORT_ORDERS.ASC ? 'up' : 'down'}
              visibility={cell.id === sortKey ? 'visible' : 'hidden'}
            />
          </div>
          {cell.options?.tooltip?.title && (
            <div style={styles.headInfo}>
              <Tooltip
                title={cell.options?.tooltip?.title}
                offsetMargin={8}
                position={cell.options?.tooltip?.position}
                style={{ ...cell.options?.tooltip?.style }}
                backgroundColor={cell.options?.tooltip?.backgroundColor}
                borderRadius={cell.options?.tooltip?.borderRadius}
                textStyle={cell.options?.tooltip?.textStyle}
                positionStyles={cell.options?.tooltip?.positionStyles}
              >
                <Icon color={COLORS.dark_800} name="info" width={20} height={20} />
              </Tooltip>
            </div>
          )}
        </TableHeader>
      );
    })}
  </TableRow>
);

/**
 * @param {number} [numberOfRows]
 * @param {number} [numberOfColumns]
 * @returns a grid of loading skeletons matching the provided number of rows and columns
 */
const LoadingSkeletons = ({ numberOfRows = 3, numberOfColumns = 1 }) => {
  return [...Array(numberOfRows)].map((row, rowIndex) => {
    return (
      <TableRow key={rowIndex} style={styles.skeletonRow}>
        {[...Array(numberOfColumns)].map((column, cellIndex) => {
          return (
            <TableData key={cellIndex} style={styles.skeletonCell}>
              <Skeleton height={30} />
            </TableData>
          );
        })}
      </TableRow>
    );
  });
};

/**
 * Get head cell by id
 * @param {object} headCellsById
 * @param {string} id
 * @returns {object} head cell
 */
const getHeadCell = (headCellsById, id) => headCellsById[id];

/**
 * Sort the data based on the sort key and sort order.
 * The sort function handles both strings and numerical values.
 * @param {object[]} rows
 * @param {string} sortKey
 * @param {string} sortOrder
 * @param {string} [secondarySortKey]
 * @returns{object[]} sorted rows
 */
const sortData = ({
  data,
  sortKey,
  sortOrder,
  secondarySortKey = null,
  sortFormatter = null,
}) => {
  if (data?.length === 0) return [];
  if (!sortKey || !sortOrder) return data;

  const sortDirectionValue = sortOrder === SORT_ORDERS.DESC ? -1 : 1;

  const getSortValue = (value) => {
    if (value === undefined || value === null) return null;
    if (typeof value === 'string') return value.toLowerCase();
    return value;
  };

  const compareSortValues = (aValue, bValue) => {
    if (bValue === null && aValue !== null) {
      return 0 + sortDirectionValue;
    }

    if (aValue === null && bValue !== null) {
      return 0 - sortDirectionValue;
    }

    if (aValue > bValue) {
      return 0 + sortDirectionValue;
    }

    if (aValue < bValue) {
      return 0 - sortDirectionValue;
    }

    return 0;
  };

  return [...data].sort((a, b) => {
    let getValue = (value) => getSortValue(value[sortKey]);
    if (typeof sortFormatter === 'function') getValue = sortFormatter(sortOrder);
    const aValue = getValue(a);
    const bValue = getValue(b);

    if (aValue === bValue) {
      if (secondarySortKey) {
        const secondarySortA = getSortValue(a[secondarySortKey]);
        const secondarySortB = getSortValue(b[secondarySortKey]);
        return compareSortValues(secondarySortA, secondarySortB);
      }
      return 0;
    }

    return compareSortValues(aValue, bValue);
  });
};

const handleTooltipPositionStyles = (tooltipHeight, offsetMargin) => ({
  positionStyle: {
    top: (tooltipHeight / 2) + offsetMargin,
    transform: 'translate(0, -100%)',
    marginLeft: 25,
    pointerEvents: 'none',
  },
  arrowPositionStyle: {
    transform: `rotate(-90deg) translate(-${(tooltipHeight / 2)-8}px, -18px)`,
  },
});

export const styles = {
  bodyCellLeftImage: {
    width: 48,
    height: 48,
    backgroundColor: COLORS.blackLight,
    color: COLORS.white,
    fontSize: 16,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    flex: '0 0 auto',
  },
  bodyCellSubtitle: {
    fontSize: 12,
    marginLeft: 8,
    color: COLORS.greyDark,
  },
  bodyCellFlexWrapper: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: 24,
  },
  bodyRow: {
    height: '40px',
  },
  ellipsisContainer: {
    display: 'inline-block',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  headCell: {
    cursor: 'pointer',
    textAlign: 'left',
    position: 'relative',
    textTransform: 'uppercase',
    WebkitTouchCallout: 'none',
    WebkitUserSelect: 'none',
    KhtmlUserSelect: 'none',
    MozUserSelect: 'none',
    MsUserSelect: 'none',
    UserSelect: 'none',
  },
  headCellContent: {
    alignItems: 'center',
    display: 'flex',
    marginRight: '16px',
  },
  headInfo: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    right: 8,
  },
  headRow: {
    backgroundColor: COLORS.white,
    height: '40px',
  },
  noDataContainer: {
    backgroundColor: COLORS.white,
    height: '40px',
  },
  tooltip: {
    padding: '4px 8px',
    width: 'auto',
    whiteSpace: 'nowrap',
  },
  tooltipIndicator: {
    display: 'inline-block',
    marginRight: 8,
    position: 'relative',
    top: 2,
  },
  skeletonCell: {
    paddingRight: '16px',
  },
  skeletonRow: {
    backgroundColor: COLORS.white,
    height: '40px',
  },
  table: {
    tableLayout: 'fixed',
  },
};

const getLabels = (getText) => ({
  noData: getText('sortableTable.noData'),
});

export default SortableTable;
